// frontend/src/components/CompleteSessionModal.tsx
import { useState, useEffect } from "react";
import { X, Receipt, CreditCard, Banknote, ArrowRightLeft, Mail, MessageSquare, Smartphone, Ban } from "lucide-react";
import { useTranslation } from "react-i18next";

interface CompleteSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: {
    amountPaid: number;
    paymentMethod: string;
    deliveryMethod: string;
    actualDuration: number;
  }) => void;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  estimatedDuration?: number;
  isLoading?: boolean;
  defaultDeliveryMethod?: string;
  autoSendInvoice?: boolean;
}
 
export function CompleteSessionModal({
  isOpen,
  onClose,
  onConfirm,
  clientName,
  clientEmail,
  clientPhone,
  estimatedDuration = 30,
  isLoading = false,
  defaultDeliveryMethod = "NONE",
  autoSendInvoice = false,
}: CompleteSessionModalProps) {
  const { t } = useTranslation();
 
  const [amountPaid, setAmountPaid] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [deliveryMethod, setDeliveryMethod] = useState("NONE");
  const [actualDuration, setActualDuration] = useState(estimatedDuration.toString());

  // Sync state when props change or modal opens
  useEffect(() => {
    if (isOpen) {
      setDeliveryMethod(autoSendInvoice ? defaultDeliveryMethod : "NONE");
      setActualDuration(estimatedDuration.toString());
      setAmountPaid("");
    }
  }, [isOpen, autoSendInvoice, defaultDeliveryMethod, estimatedDuration]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm({
      amountPaid: parseFloat(amountPaid) || 0,
      paymentMethod,
      deliveryMethod,
      actualDuration: parseInt(actualDuration) || estimatedDuration,
    });
  };

  const paymentMethods = [
    { value: "CASH", label: t("common.cash") || "Espèces", icon: Banknote, color: "text-green-600 bg-green-50 border-green-200" },
    { value: "CARD", label: t("common.card") || "Carte", icon: CreditCard, color: "text-blue-600 bg-blue-50 border-blue-200" },
    { value: "TRANSFER", label: t("common.transfer") || "Virement", icon: ArrowRightLeft, color: "text-sky-700 bg-slate-50 border-slate-200" },
    { value: "OTHER", label: t("common.other") || "Autre", icon: Receipt, color: "text-gray-600 bg-gray-50 border-gray-200" },
  ];

  const deliveryMethods = [
    { value: "EMAIL", label: t("common.email") || "Email", icon: Mail, color: "text-sky-700 bg-slate-50 border-slate-200", disabled: !clientEmail },
    { value: "WHATSAPP", label: "WhatsApp", icon: MessageSquare, color: "text-green-600 bg-green-50 border-green-200", disabled: !clientPhone },
    { value: "SMS", label: "SMS", icon: Smartphone, color: "text-blue-600 bg-blue-50 border-blue-200", disabled: !clientPhone },
    { value: "NONE", label: t("modal.complete.none") || "Aucun", icon: Ban, color: "text-gray-600 bg-gray-50 border-gray-200", disabled: false },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 text-white p-5 rounded-t-2xl flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              {t("modal.complete.title") || "Clôturer la prestation"}
            </h2>
            <p className="text-sm opacity-90 mt-1">
              {t("modal.complete.desc", { name: clientName })}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-5 space-y-6">
          {/* Amount */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("modal.complete.amount") || "Montant payé (€)"}
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-lg">€</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amountPaid}
                onChange={(e) => setAmountPaid(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl text-lg font-semibold focus:ring-2 focus:ring-sky-600 focus:border-sky-600 transition-all"
                placeholder="0.00"
                autoFocus
              />
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("modal.complete.duration") || "Durée réelle (min)"}
            </label>
            <input
              type="number"
              min="1"
              value={actualDuration}
              onChange={(e) => setActualDuration(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-semibold focus:ring-2 focus:ring-sky-600 focus:border-sky-600 transition-all"
            />
          </div>

          {/* Payment method */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("modal.complete.method") || "Mode de paiement"}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {paymentMethods.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setPaymentMethod(m.value)}
                  className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all font-medium text-sm ${
                    paymentMethod === m.value
                      ? `${m.color} border-current ring-2 ring-current/20`
                      : "border-gray-200 text-gray-500 hover:border-gray-300"
                  }`}
                >
                  <m.icon className="w-4 h-4" />
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Delivery */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("modal.complete.delivery") || "Envoi du reçu PDF"}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {deliveryMethods.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  disabled={m.disabled}
                  onClick={() => setDeliveryMethod(m.value)}
                  className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all font-medium text-sm ${
                    m.disabled
                      ? "opacity-40 cursor-not-allowed border-gray-200 text-gray-400"
                      : deliveryMethod === m.value
                        ? `${m.color} border-current ring-2 ring-current/20`
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                  }`}
                >
                  <m.icon className="w-4 h-4" />
                  {m.label}
                </button>
              ))}
            </div>
            {deliveryMethod === "EMAIL" && clientEmail && (
              <p className="text-xs text-gray-500 mt-2">📧 → {clientEmail}</p>
            )}
            {(deliveryMethod === "WHATSAPP" || deliveryMethod === "SMS") && clientPhone && (
              <p className="text-xs text-gray-500 mt-2">📱 → {clientPhone}</p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold text-base hover:bg-slate-800 disabled:opacity-50 transition-all duration-200 shadow-lg"
          >
            {isLoading
              ? t("common.loading")
              : t("modal.complete.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
